/**
 * Date and time formatting utilities.
 */

/**
 * Formats a date to DD.MM format (e.g., "15.12")
 */
export function formatDateDDMM(date: Date): string {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}.${month}`;
}

/**
 * Formats time to HH:MM format (e.g., "22:30")
 */
export function formatTimeHHMM(date: Date): string {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
}

/**
 * Formats a date to "7 Jan" format
 */
export function formatDateDayMonth(date: Date): string {
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${day} ${month}`;
}

/**
 * Formats a timestamp to DD.MM.YYYY format.
 */
export const formatDateTime = (date: Date): string => {
    const months = date.getMonth();
    const days = date.getDate();

    const MM = months + 1 < 10 ? `0${months + 1}` : months + 1;
    const DD = days < 10 ? `0${days}` : days;
    const YYYY = date.getFullYear();

    return `${DD}.${MM}.${YYYY}`;
};

/**
 * Returns today's date as an ISO date string (YYYY-MM-DD).
 */
export function todayISODate(): string {
    return new Date().toISOString().slice(0, 10);
}

/**
 * Checks if a date is today
 */
export function isToday(date: Date): boolean {
    const today = new Date();
    return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
    );
}

/**
 * Checks if a date is tomorrow
 */
export function isTomorrow(date: Date): boolean {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return (
        date.getDate() === tomorrow.getDate() &&
        date.getMonth() === tomorrow.getMonth() &&
        date.getFullYear() === tomorrow.getFullYear()
    );
}

/**
 * Formats upcoming match date/time based on proximity
 * - Today: "Today 22:30"
 * - Tomorrow: "Tomorrow 22:30"
 * - Future: "DD.MM, HH:MM"
 */
export function formatUpcomingMatchTime(date: Date): string {
    const time = formatTimeHHMM(date);

    if (isToday(date)) {
        return `Today ${time}`;
    }

    if (isTomorrow(date)) {
        return `Tomorrow ${time}`;
    }

    // Future date: show date and time
    const dateStr = formatDateDDMM(date);
    return `${dateStr}, ${time}`;
}

/**
 * Formats a date string as relative time (e.g., "Just now", "5m ago", "2h ago", "3d ago")
 * or falls back to "DD Mon" format for dates older than 7 days.
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
